import type React from "react";

import * as _ from "lodash";

import Sentry from "@similarweb/sentry-logger";

import { keywordGroupsQueryClient } from "app/services/keyword-groups";
import { getCountries } from "app/components/filters-bar/utils";
import { DefaultFetchService } from "app/services/fetchService";
import { RecentService } from "app/services/recent/recentService";

export enum EStepState {
    INCOMPLETE = "incomplete",
    SKIP = "skip",
    COMPLETED = "completed",
}

export enum WizardRole {
    General = "General",
    Seo = "Seo",
}

export interface IStepState {
    index: number;
    state: EStepState;
}

export interface IBaseStep {
    title: string;
    description: string;
    additions?: React.ReactNode;
    buttonsList?: React.ReactNode;
}

interface IRecentParams {
    duration: string;
    country: number;
    key: string;
    isWWW: string;
}

export interface IStepMetadata {
    role: WizardRole;
    index: number;
    isOpen: boolean;
    isOpening: boolean;
    onStepSkip: () => void;
    stepState: EStepState;
    onClickHoverButton: () => void;
    onStepComplete: (guid: string) => void;
    trackWithGoal: (guid: string, action: string, replacements?: Record<string, string>) => void;
}

export interface ICompletionStep {
    title: string;
    illustration: string;
    description?: string;
    buttonText?: string;
    onClick?: () => void;
}

export interface IActivationWizard {
    steps: Array<React.FC<IStepMetadata>>;
    getCompletionStep: (allSkipped: boolean) => ICompletionStep;
}

export const getRecentParams = (): IRecentParams | null => {
    const recentSearches = RecentService.getRecents();
    if (!recentSearches.length) {
        return null;
    }

    const recentFirstSearchData = recentSearches[0].data;
    return Object.fromEntries(
        Object.entries({
            duration: recentFirstSearchData.duration,
            country: recentFirstSearchData.country,
            key: `${recentFirstSearchData.mainItem}${
                recentFirstSearchData.comparedItems && recentFirstSearchData.comparedItems.length > 0
                    ? "," + _.toString(recentFirstSearchData.comparedItems)
                    : ""
            }`,
            isWWW: "*",
        }).filter(([_, val]) => val !== undefined),
    ) as unknown as IRecentParams;
};

export const createExampleKeywordsList = async () => {
    const KEYWORDS_GENERATOR_URL = "/api/googleKeywordGenerator/broadMatch";
    const fetchService = DefaultFetchService.getInstance();
    try {
        const response: any = await fetchService.get(KEYWORDS_GENERATOR_URL, {
            keyword: "face mask",
            country: getCountries()?.[0].id ?? 999,
        });
        const keywords = response?.records?.slice?.(0, 200).map(({ keyword }) => keyword) ?? [];
        const keywordsList = await keywordGroupsQueryClient.createGroup({
            Keywords: keywords,
            Name: "Face mask",
        });

        return keywordsList.Id;
    } catch (error) {
        Sentry.errorTracker({
            message: "Error on creating example keywords list",
            e: error,
            level: "error",
        });
    }
};
